import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

const VIEW = 280; // on-screen square, px
const MAX_ZOOM = 4;

// Lets the user position and zoom a picked image inside a square (or circle, for profile pictures)
// before it's uploaded. Calls onSave(blob) with a square JPEG `output` px wide, or onCancel().
const PfpCropper = ({ file, title = 'Profile picture', circle = true, output = 512, saving, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [image, setImage] = useState(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // image centre relative to the view centre

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setFailed(true);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Scale at zoom 1: the image just covers the square.
  const baseScale = image ? Math.max(VIEW / image.width, VIEW / image.height) : 1;

  // Keeps the image covering the whole square.
  const clamp = (pos, nextZoom) => {
    if (!image) return pos;
    const scale = baseScale * nextZoom;
    const maxX = (image.width * scale - VIEW) / 2;
    const maxY = (image.height * scale - VIEW) / 2;
    return { x: Math.min(maxX, Math.max(-maxX, pos.x)), y: Math.min(maxY, Math.max(-maxY, pos.y)) };
  };

  const draw = (canvas, size) => {
    const factor = size / VIEW;
    const scale = baseScale * zoom * factor;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size, size);
    const w = image.width * scale;
    const h = image.height * scale;
    ctx.drawImage(image, size / 2 + offset.x * factor - w / 2, size / 2 + offset.y * factor - h / 2, w, h);
  };

  useEffect(() => {
    if (image && canvasRef.current) draw(canvasRef.current, VIEW);
  });

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, start: offset };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset(clamp({ x: drag.start.x + e.clientX - drag.x, y: drag.start.y + e.clientY - drag.y }, zoom));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const changeZoom = (e) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setOffset((pos) => clamp(pos, next));
  };

  const save = () => {
    const out = document.createElement('canvas');
    out.width = output;
    out.height = output;
    draw(out, output);
    out.toBlob((blob) => blob && onSave(blob), 'image/jpeg', 0.9);
  };

  return (
    <Modal show onHide={saving ? undefined : onCancel} centered>
      <Modal.Header closeButton={!saving}><Modal.Title>{title}</Modal.Title></Modal.Header>
      <Modal.Body className="text-center">
        {failed && <p className="text-danger">Couldn't read that image. Try a JPEG or PNG.</p>}
        {!failed && !image && <Spinner animation="border" />}
        {image && (
          <React.Fragment>
            <div style={{ position: 'relative', width: VIEW, height: VIEW, margin: '0 auto', overflow: 'hidden' }}>
              <canvas
                ref={canvasRef}
                width={VIEW}
                height={VIEW}
                aria-label="Drag to position your picture"
                style={{ display: 'block', cursor: 'grab', touchAction: 'none', background: '#fff' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              />
              {/* Dims everything outside the circle that will be shown. */}
              {circle && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  boxShadow: '0 0 0 400px rgba(0, 0, 0, 0.55)',
                  pointerEvents: 'none',
                }} />
              )}
            </div>
            <Form.Group className="mt-3 mb-0" controlId="pfp-zoom">
              <Form.Label className="small text-muted">Drag to move, slide to zoom</Form.Label>
              <Form.Control type="range" min="1" max={MAX_ZOOM} step="0.01" value={zoom} onChange={changeZoom} disabled={saving} />
            </Form.Group>
          </React.Fragment>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onCancel}>Cancel</Button>
        <Button disabled={!image || saving} onClick={save}>{saving ? 'Uploading…' : 'Save'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PfpCropper;
